import type { VisitView } from "../../../server/modules/homecare/visits";
import { hintsFor } from "../../ui/hints";
import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { photoImg } from "../../ui/face";
import { firstPageParams, list, type Column } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import { earlierLabel, threadShown } from "../../../server/rules/folds";
import { openModal } from "../../ui/modal";
import { card, chip, ib, muted, table } from "../../ui/record";
import { area, field, pills, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";
import { careTeamCard } from "./careteam";
import { appointmentsDuringCareCard, patientAppointmentCards } from "./away";
import { coverageView, validateView } from "./validate";

// Scheduling visits (brief 1D-1, mockup homecare/visits, the Schedule visits sheet (M.type
// "sched") and its preview (schedPrev), and the patient's Visits tab):
// - Home Care › Visits: the board by stage (Scheduled, In progress, Completed, Documented,
//   Validated) and the list (UI kit), with filters by patient, caregiver, client, date and flagged,
//   and the span of days; Schedule visits opens the sheet. A row opens the visit.
// - The sheet: Caregiver, Patient, Service, Shift, Repeat (Just this day, On these weekdays, Every
//   day for a week, Every day until, A number of times), Starting, Until, How many times, Every how
//   many days, and the preview: how many and the dates, "n outside availability" or "All inside
//   availability", and every date with what it warns about. Nothing it warns about stops Save.
// - A visit: change this visit or this and later, reassign, cancel with who and why, mark an RN
//   visit supervisory; each an icon button with its hover tip.
// - The patient's Visits tab: Upcoming and Past, When, Who, Billed by, Stage, with Schedule visit,
//   and the Care team · caregivers card (brief 1D-3b, ./careteam.ts).
// - Brief 1D-3b on the visit sheet: the not-oriented note with Add an orientation visit first, and
//   the office recording a missed check-out with the real time and a reason.
// - Brief 1D-4: the views To validate and 24/7 (./validate.ts) beside Day, Week, Month and By
//   stage; 24/7 gaps and missing notes on the issues bar; a reason for the missed check-out.

interface Sheet {
  patients: { id: string; label: string }[];
  caregivers: { id: string; label: string; title: string | null }[];
  patient: { id: string; name: string } | null;
  services: { value: string; label: string; onPlan: boolean }[];
  shifts: { value: string; label: string }[];
  repeats: { value: string; label: string }[];
  days: { value: string; label: string }[];
  cancelledBy: { value: string; label: string }[];
}
interface Flag {
  key: string;
  words: string;
}
interface Preview {
  count: number;
  noAvailability: boolean;
  words: string;
  outside: number;
  allInside: boolean;
  warnings: { key: string; words: string; n: number }[];
  dates: { day: string; when: string; flags: Flag[] }[];
}

const AVAILABILITY = "outside_availability";
const today = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const plusDays = (day: string, n: number) =>
  new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** The flags a visit carries, as small warning chips (never a refusal). */
export const flagChips = (flags: readonly Flag[]) =>
  flags.length
    ? el(
        "div",
        { class: "vflags", "aria-label": "Flags" },
        ...flags.map((f) => chip(f.words, "warn")),
      )
    : null;

const stageChip = (v: VisitView) =>
  chip(
    v.stage,
    v.status === "cancelled" || v.status === "missed"
      ? "plain"
      : v.status === "validated"
        ? "ok"
        : "info",
  );

// ---- The Schedule visits sheet --------------------------------------------------------------------

/**
 * Schedule visits (mockup M.type "sched", homecare): set it once and repeat it; the preview shows
 * every date before Save. `patientId` picks the patient (the patient's Visits tab).
 */
export async function scheduleVisits(opts: { patientId?: string; done?: () => void }) {
  const load = (pid?: string) =>
    api<Sheet>("GET", `/api/visits/sheet${pid ? `?patient=${encodeURIComponent(pid)}` : ""}`);
  const first = await load(opts.patientId);
  if (!first.ok)
    return toast("Visits can't be scheduled", (first.body as { error?: string }).error);
  let sheet = first.body;
  if (!opts.patientId && sheet.patients[0]) {
    const again = await load(sheet.patients[0].id);
    if (again.ok) sheet = again.body;
  }

  const caregiver = select(
    "Caregiver",
    [["", "Unassigned (fill it later)"], ...sheet.caregivers.map((c) => [c.id, c.label] as const)],
    "",
  );
  const patient = select(
    "Patient",
    sheet.patients.map((p) => [p.id, p.label] as const),
    sheet.patient?.id ?? "",
  );
  const serviceSlot = el("div");
  const shiftSlot = el("div", { class: "wide" });
  const customSlot = el("div", { class: "formgrid wide" });
  const repeat = pills(
    "Repeat",
    "radio",
    sheet.repeats.map((r) => [r.value, r.label] as const),
    ["weekdays"],
  );
  const days = pills(
    "On these weekdays",
    "checkbox",
    sheet.days.map((d) => [d.value, d.label] as const),
    ["1", "3", "5"],
  );
  const start = field("Starting", plusDays(today(), 1), { type: "date" });
  const until = field("Until", plusDays(today(), 28), { type: "date" });
  const times = field("How many times", "4", { type: "number", min: "1", max: "400" });
  const every = field("Every how many days", "1", { type: "number", min: "1", max: "365" });
  const startTime = field("Start", "08:00", { type: "time" });
  const endTime = field("End", "14:00", { type: "time" });
  const prev = el("div", { "aria-live": "polite", "aria-label": "Preview" });
  const sugs = el("div");
  const suggested = el(
    "details",
    { class: "card wide", style: "margin:0", open: true },
    el(
      "summary",
      {},
      el("b", {}, icon("zap"), " Suggested caregivers"),
      " ",
      el("span", { class: "small muted" }, `for ${sheet.patient?.name ?? "this patient"}`),
    ),
    sugs,
  );

  let service = select("Service", [], "");
  let shift = pills("Shift", "radio", [], []);
  const drawPatientParts = () => {
    service = select(
      "Service (from the care plan)",
      sheet.services.map((s) => [s.value, s.label] as const),
      sheet.services[0]?.value ?? "",
    );
    service.input.addEventListener("change", () => void preview());
    serviceSlot.replaceChildren(service.el);
    shift = pills(
      "Shift",
      "radio",
      sheet.shifts.map((s) => [s.value, s.label] as const),
      [sheet.shifts[0]?.value ?? "custom"],
    );
    for (const i of shift.inputs) i.addEventListener("change", () => redraw());
    shiftSlot.replaceChildren(shift.el);
  };
  drawPatientParts();

  const shown = (n: HTMLElement, on: boolean) => {
    // Not `hidden`: a field's own display would win over it.
    n.style.display = on ? "" : "none";
  };
  function redraw() {
    const r = repeat.values()[0] ?? "once";
    shown(days.el, r === "weekdays");
    shown(until.el, r === "weekdays" || r === "daily");
    shown(times.el, r === "times");
    shown(every.el, r === "times");
    shown(customSlot, (shift.values()[0] ?? "") === "custom");
    void preview();
  }
  for (const i of repeat.inputs) i.addEventListener("change", redraw);
  for (const i of [
    ...days.inputs,
    start.input,
    until.input,
    times.input,
    every.input,
    startTime.input,
    endTime.input,
    caregiver.input,
  ])
    i.addEventListener("change", () => void preview());
  patient.input.addEventListener("change", async () => {
    const res = await load(patient.input.value);
    if (res.ok) sheet = res.body;
    drawPatientParts();
    redraw();
  });

  const payload = () => ({
    patientId: patient.input.value,
    service: service.input.value,
    caregiverId: caregiver.input.value,
    shift: shift.values()[0] ?? "custom",
    start: startTime.input.value,
    end: endTime.input.value,
    repeat: repeat.values()[0],
    days: days.values().map(Number),
    startsOn: start.input.value,
    untilOn: until.input.value,
    times: times.input.value,
    everyDays: every.input.value,
  });

  let seq = 0;
  let sugSeq = 0;
  /** Suggested caregivers for the first date (mockup: Pick fills the caregiver). */
  async function suggest() {
    const mine = ++sugSeq;
    const res = await api<{ patient: string; suggestions: Sug[] }>(
      "POST",
      "/api/visits/suggest",
      payload(),
    );
    if (mine !== sugSeq) return;
    if (!res.ok) return sugs.replaceChildren(muted(res.body.error ?? ""));
    const summary = suggested.querySelector("summary .muted");
    if (summary) summary.textContent = `for ${res.body.patient}`;
    sugs.replaceChildren(
      suggestionList(res.body.suggestions, (g) => {
        if (g.id === caregiver.input.value) return chip("Picked", "ok");
        const b = el("button", { type: "button", class: "btn sm" }, "Pick");
        b.addEventListener("click", () => {
          caregiver.input.value = g.id;
          void preview();
        });
        return b;
      }),
    );
  }
  async function preview() {
    const mine = ++seq;
    void suggest();
    if (!patient.input.value) {
      prev.replaceChildren(
        el("div", { class: "note" }, el("p", {}, "Pick the patient to see every date.")),
      );
      return;
    }
    const res = await api<{ preview: Preview; error?: string }>(
      "POST",
      "/api/visits/preview",
      payload(),
    );
    if (mine !== seq) return;
    if (!res.ok) {
      prev.replaceChildren(el("div", { class: "note" }, el("p", {}, res.body.error ?? "")));
      return;
    }
    prev.replaceChildren(previewNote(res.body.preview));
  }

  const go = el("button", { type: "submit", class: "btn pri" }, icon("calendar"), " Schedule");
  const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
  const form = el(
    "form",
    { class: "form", style: "max-width:none", novalidate: "" },
    el(
      "div",
      { class: "formgrid" },
      caregiver.el,
      patient.el,
      el("div", { class: "wide" }, serviceSlot),
      suggested,
      shiftSlot,
    ),
    customSlot,
    el("div", { class: "formgrid" }, repeat.el, days.el, start.el, until.el, times.el, every.el),
    prev,
  );
  customSlot.append(startTime.el, endTime.el);
  const m = openModal({
    title: "Schedule visits",
    sub: "Set it once and repeat it. The preview shows every date before saving.",
    body: [
      el(
        "p",
        { class: "muted small", style: "margin:0" },
        "Only patients with an open episode are listed.",
      ),
      form,
    ],
    actions: [go, cancel],
  });
  cancel.addEventListener("click", () => m.close());
  redraw();
  caregiver.input.focus();

  const submit = async () => {
    go.disabled = true;
    const res = await api<{ scheduled: number; preview: Preview; error?: string }>(
      "POST",
      "/api/visits",
      payload(),
    );
    go.disabled = false;
    if (!res.ok) return refuse(m.note, res.body.error);
    m.close();
    const p = res.body.preview;
    toast(
      `${res.body.scheduled} visit${res.body.scheduled === 1 ? "" : "s"} scheduled as drafts`,
      p.noAvailability
        ? "No availability on file for this caregiver · publish to tell them"
        : p.outside
          ? `${p.outside} outside availability: flagged · publish to tell them`
          : "Publish to tell the caregiver",
    );
    opts.done?.();
  };
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    void submit();
  });
  go.addEventListener("click", (e) => {
    e.preventDefault();
    void submit();
  });
}

/** The preview (mockup schedPrev): how many, the dates, the warnings, then every date. */
function previewNote(p: Preview): HTMLElement {
  const others = p.warnings.filter((w) => w.key !== AVAILABILITY);
  return el(
    "div",
    { class: "note" },
    el(
      "p",
      {},
      el("b", {}, `${p.count} visit${p.count === 1 ? "" : "s"}`),
      p.words.replace(/^\d+ visits?/, ""),
      p.noAvailability
        ? el("span", {}, " · ", chip("No availability on file for this caregiver", "warn"))
        : p.outside
          ? el("span", {}, " · ", chip(`${p.outside} outside availability`, "warn"))
          : p.allInside
            ? el("span", {}, " · ", chip("All inside availability", "ok"))
            : null,
    ),
    others.length
      ? el(
          "p",
          { class: "small", style: "margin:4px 0 0" },
          ...others.flatMap((w) => [chip(`${w.n} · ${w.words}`, "warn"), " "]),
        )
      : null,
    p.dates.length
      ? el(
          "details",
          { class: "small", style: "margin-top:6px" },
          el("summary", {}, "Every date"),
          el(
            "ul",
            { class: "vdates", "aria-label": "Every date" },
            ...p.dates.map((d) => el("li", {}, el("b", {}, d.when), flagChips(d.flags))),
          ),
        )
      : null,
    p.warnings.length
      ? el(
          "p",
          { class: "small muted", style: "margin:6px 0 0" },
          "Each one is a flag on the visit.",
        )
      : null,
  );
}

// ---- A visit: change, reassign, cancel, supervisory ---------------------------------------------------

/** The icon buttons a scheduled visit has (mockup ib with its hover tip). */
export function visitActions(v: VisitView, changed: () => void): HTMLElement {
  if (v.status !== "scheduled") return el("span");
  const acts: HTMLElement[] = [
    ib("pencil", "Change this visit", () => void changeVisit(v, "change", changed)),
    ib("person", "Reassign", () => void changeVisit(v, "reassign", changed)),
    ib("x-circle", "Cancel this visit", () => void cancelVisit(v, changed)),
  ];
  if (v.canSupervise)
    acts.push(
      ib(
        "shield-check",
        v.supervisory ? "Not a supervisory visit" : "Mark as supervisory visit",
        () => void markSupervisory(v, !v.supervisory, changed),
      ),
    );
  return el("div", { class: "vacts" }, ...acts);
}

async function changeVisit(v: VisitView, how: "change" | "reassign", changed: () => void) {
  const res = await api<Sheet>("GET", `/api/visits/sheet?patient=${v.patient.id}`);
  if (!res.ok) return toast("This visit can't be changed");
  const s = res.body;
  const scope = pills(
    "Change",
    "radio",
    [["this", "This visit"], ...(v.inSeries ? ([["later", "This and later"]] as const) : [])],
    ["this"],
  );
  const caregiver = select(
    "Caregiver",
    s.caregivers.map((c) => [c.id, c.label] as const),
    v.caregiver.id,
  );
  const shift = pills(
    "Shift",
    "radio",
    [["", "Keep the times"], ...s.shifts.map((x) => [x.value, x.label] as const)],
    [""],
  );
  const start = field("Start", v.start, { type: "time" });
  const end = field("End", v.end, { type: "time" });
  const day = field("Day", v.day, { type: "date" });
  const custom = el("div", { class: "formgrid wide" }, start.el, end.el);
  // A visit booked as the wrong service (brief 1E-23, mockup vchgModal): changed with a reason.
  const svcs = s.services.some((x) => x.value === v.choice)
    ? s.services
    : [{ value: v.choice, label: v.service, onPlan: false }, ...s.services];
  const service = select(
    "Service",
    svcs.map((x) => [x.value, x.label] as const),
    v.choice,
  );
  const reason = field("Reason", "", {
    maxlength: "300",
    placeholder: "e.g. Booked as the wrong service",
  });
  const draw = () => {
    custom.style.display = shift.values()[0] === "custom" ? "" : "none";
    day.el.style.display = scope.values()[0] === "later" ? "none" : "";
  };
  for (const i of [...shift.inputs, ...scope.inputs]) i.addEventListener("change", draw);
  const save = el("button", { type: "submit", class: "btn pri" }, icon("check"), " Save");
  const body =
    how === "reassign"
      ? [scope.el, caregiver.el]
      : [scope.el, service.el, caregiver.el, shift.el, custom, day.el, reason.el];
  const m = openModal({
    title: how === "reassign" ? "Reassign" : "Change this visit",
    sub: `${v.patient.name} · ${v.when} · ${v.caregiver.label}`,
    body: [el("form", { class: "form", style: "max-width:none" }, ...body)],
    actions: [save],
    wide: true,
  });
  draw();
  save.addEventListener("click", async (e) => {
    e.preventDefault();
    const sh = shift.values()[0] ?? "";
    const serviceChanged = how === "change" && service.input.value !== v.choice;
    if (serviceChanged) {
      const c = await api<{ changed: number; error?: string }>(
        "PATCH",
        `/api/visits/${v.id}/service`,
        { service: service.input.value, scope: scope.values()[0], reason: reason.input.value },
      );
      if (!c.ok) return refuse(m.note, c.body.error);
      v.choice = service.input.value;
    }
    const r = await api<{ changed: number; error?: string }>("PUT", `/api/visits/${v.id}`, {
      scope: scope.values()[0],
      caregiverId: caregiver.input.value !== v.caregiver.id ? caregiver.input.value : undefined,
      shift: how === "change" && sh ? sh : undefined,
      start: start.input.value,
      end: end.input.value,
      day: how === "change" && day.input.value !== v.day ? day.input.value : undefined,
    });
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast(
      how === "reassign" ? "Reassigned" : "Visit changed",
      serviceChanged
        ? "Its flags are checked again"
        : `${r.body.changed} visit${r.body.changed === 1 ? "" : "s"}`,
    );
    changed();
  });
}

async function cancelVisit(v: VisitView, changed: () => void) {
  const res = await api<Sheet>("GET", `/api/visits/sheet?patient=${v.patient.id}`);
  const who = res.ok ? res.body.cancelledBy : [];
  const scope = pills(
    "Cancel",
    "radio",
    [["this", "This visit"], ...(v.inSeries ? ([["later", "This and later"]] as const) : [])],
    ["this"],
  );
  const byWhom = pills(
    "Who cancelled",
    "radio",
    who.map((w) => [w.value, w.label] as const),
    [],
  );
  const why = area("Why", "");
  const go = el("button", { type: "submit", class: "btn pri" }, icon("x-circle"), " Cancel visit");
  const m = openModal({
    title: "Cancel this visit",
    sub: `${v.patient.name} · ${v.when}. A cancelled visit isn't billed.`,
    body: [el("form", { class: "form", style: "max-width:none" }, scope.el, byWhom.el, why.el)],
    actions: [go],
  });
  go.addEventListener("click", async (e) => {
    e.preventDefault();
    const r = await api<{ cancelled: number; error?: string }>(
      "POST",
      `/api/visits/${v.id}/cancel`,
      { scope: scope.values()[0], byWhom: byWhom.values()[0], why: why.input.value },
    );
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast(
      `${r.body.cancelled} visit${r.body.cancelled === 1 ? "" : "s"} cancelled`,
      "Kept with who and why",
    );
    changed();
  });
}

async function markSupervisory(v: VisitView, on: boolean, changed: () => void) {
  const r = await api<{ error?: string }>("POST", `/api/visits/${v.id}/supervisory`, {
    supervisory: on,
  });
  if (!r.ok) return toast("Not changed", r.body.error);
  toast(
    on ? "Marked as a supervisory visit" : "No longer a supervisory visit",
    on ? "The aide supervision clock runs from it" : "",
  );
  changed();
}

// ---- The visit sheet (mockup vkModal) ------------------------------------------------------------------

interface Sug {
  rank: number;
  id: string;
  name: string;
  title: string | null;
  reasons: string[];
  notes: string[];
  misses: number;
}
interface VisitSheet {
  visit: VisitView;
  canSchedule: boolean;
  fromPlan: boolean;
  suggestions: Sug[];
  caregivers: { id: string; label: string }[];
  activity: { words: string; by: string; at: string }[];
  chat: { who: string; body: string; at: string }[];
  chatBy: "text" | "note" | "none";
  orientation: {
    title: string;
    words: string;
    set?: { visitId: string; words: string } | null;
  } | null;
  canRecordCheckOut: boolean;
  /** The company's reasons for correcting a time (brief 1D-4): the record goes there too. */
  correctionReasons: string[];
  /** The patient's photo (brief 1E-17), for people who see the patient. */
  patientPhoto: string | null;
  /** Brief 1E-19: who pays for this visit or ride (the feature on). */
  whoPays: {
    contractId: string | null;
    words: string;
    choices: { value: string; label: string }[];
  } | null;
}

/** The ranked suggestions with their reasons and notes (mockup sgList); `act` is each row's action. */
export function suggestionList(list: readonly Sug[], act: (s: Sug) => Node): HTMLElement {
  return el(
    "div",
    {},
    el(
      "div",
      { class: "vksg", role: "list", "aria-label": "Suggested caregivers" },
      ...list.map((g) =>
        el(
          "div",
          { class: "sg", role: "listitem" },
          el(
            "div",
            {},
            el("span", { class: "rk" }, String(g.rank)),
            el("b", {}, g.name),
            " ",
            el("span", { class: "small muted" }, g.title ?? ""),
            g.reasons.length ? el("div", { class: "why" }, g.reasons.join(" · ")) : null,
            g.notes.length
              ? el("div", { class: "why miss" }, `Note: ${g.notes.join(" · ")}`)
              : null,
          ),
          act(g),
        ),
      ),
    ),
    list.length
      ? null
      : el("p", { class: "muted small" }, "No caregivers to suggest yet: add caregivers first."),
    el(
      "p",
      { class: "small muted", style: "margin:6px 0 0" },
      "Ranked by the matching factors in Settings › Home Care › Scheduling.",
    ),
  );
}

/** One visit opened from the board, the issues or auto-assign (mockup vkModal). */
export async function openVisitSheet(ctx: ScreenContext, id: string, changed: () => void) {
  const res = await api<VisitSheet>("GET", `/api/visits/${id}`);
  if (!res.ok) return toast("That visit couldn't be opened", res.body.error);
  const s = res.body;
  const v = s.visit;
  const patientBtn = el("button", { type: "button", class: "btn" }, icon("heart"), " Patient");
  const again = () => {
    m.close();
    changed();
    void openVisitSheet(ctx, id, changed);
  };
  const post = async (
    path: string,
    body: unknown,
    done: string,
    sub?: (b: Record<string, unknown>) => string,
  ) => {
    const r = await api<Record<string, unknown>>("POST", `/api/visits/${v.id}/${path}`, body);
    if (!r.ok) return refuse(m.note, r.body.error);
    toast(done, sub?.(r.body));
    again();
  };
  const acts: HTMLElement[] = [];
  if (s.canSchedule) {
    acts.push(
      v.published
        ? ib(
            "eye-closed",
            "Unpublish (the caregiver is told)",
            () =>
              void post(
                "unpublish",
                {},
                "Unpublished",
                () => `${v.caregiver.name} was told it's off their schedule`,
              ),
          )
        : ib(
            "paper-airplane",
            "Publish this visit",
            () => void post("publish", {}, "Published", (b) => String(b.words ?? "")),
          ),
      ib(
        "pencil",
        "Change this visit or this and later",
        () => void changeVisit(v, "change", again),
      ),
    );
    if (v.caregiver.id)
      acts.push(
        ib(
          "person",
          "Unassign",
          () => void post("unassign", {}, "Unassigned", () => "It's in the Unassigned row"),
        ),
      );
    acts.push(ib("x-circle", "Cancel with who and why", () => void cancelVisit(v, again)));
    if (v.canSupervise)
      acts.push(
        ib(
          "shield-check",
          v.supervisory ? "Not a supervisory visit" : "Mark as supervisory visit",
          () => void markSupervisory(v, !v.supervisory, again),
        ),
      );
  }
  if (s.canRecordCheckOut)
    acts.push(
      ib("clock", "Record the missed check-out (the real time and a reason)", () =>
        recordCheckOut(v, s.correctionReasons, again),
      ),
    );
  // Not oriented to a Flag or Required patient: a note and one click to a draft orientation visit.
  let orientationNote: HTMLElement | null = null;
  // Brief 1E-30: one already set opens instead of adding another.
  const set = s.orientation?.set ?? null;
  if (s.orientation && set) {
    const open = el("button", { type: "button", class: "btn sm" }, icon("calendar"), " Open");
    open.addEventListener("click", () => {
      m.close();
      void openVisitSheet(ctx, set.visitId, changed);
    });
    orientationNote = el(
      "div",
      { class: "note", style: "margin-top:10px", role: "note" },
      el("p", {}, el("b", {}, s.orientation.title), ` ${s.orientation.words}`),
      el("p", { class: "small" }, set.words, " · ", open),
    );
  } else if (s.orientation) {
    const add = el(
      "button",
      { type: "button", class: "btn sm" },
      icon("people"),
      " Add an orientation visit first",
    );
    add.addEventListener("click", async () => {
      add.disabled = true;
      const r = await api<{ words: string; existing?: boolean; visit: { id: string } }>(
        "POST",
        `/api/visits/${v.id}/orientation`,
        {},
      );
      add.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      if (r.body.existing) {
        toast(r.body.words);
        m.close();
        return void openVisitSheet(ctx, r.body.visit.id, changed);
      }
      toast("Orientation visit added as a draft", r.body.words);
      again();
    });
    orientationNote = el(
      "div",
      { class: "note", style: "margin-top:10px", role: "note" },
      el("p", {}, el("b", {}, s.orientation.title), ` ${s.orientation.words}`),
      add,
    );
  }
  const status = el(
    "span",
    { class: "vflags", "aria-label": "Status" },
    chip(v.published ? "Published" : "Draft", v.published ? "ok" : "plain"),
    v.state === "unassigned"
      ? chip("Unassigned", "warn")
      : v.state === "confirmed"
        ? chip("Confirmed", "ok")
        : v.state === "cannot_work"
          ? chip(`Can't work${v.cannotWorkReason ? ` · ${v.cannotWorkReason}` : ""}`, "bad")
          : v.state === "waiting"
            ? chip(`Waiting for ${v.caregiver.name.split(" ")[0]}`, "warn")
            : "",
    v.supervisory ? chip("Supervisory", "info") : "",
    ...(v.state === "draft" || v.state === "unassigned" ? [] : []),
  );
  const pickAny = select(
    "Or pick anyone",
    [["", "Choose a caregiver"], ...s.caregivers.map((c) => [c.id, c.label] as const)],
    "",
  );
  pickAny.input.addEventListener("change", () => {
    if (!pickAny.input.value) return;
    void post("assign", { caregiverId: pickAny.input.value }, "Assigned", (b) =>
      (b.notes as string[] | undefined)?.length
        ? `Draft · note: ${(b.notes as string[]).join(" · ")}`
        : "Saved as a draft · publish to tell them",
    );
  });
  const suggestions = s.canSchedule
    ? el(
        "section",
        { class: "card", style: "margin:10px 0 0", "aria-label": "Suggested caregivers" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, icon("zap"), " Suggested caregivers"),
            el(
              "span",
              { class: "small muted" },
              "From the patient's intake and care plan, and each caregiver's profile, availability and history.",
            ),
          ),
        ),
        suggestionList(s.suggestions, (g) => {
          if (g.id === v.caregiver.id) return chip("Assigned", "ok");
          const b = el(
            "button",
            { type: "button", class: "btn sm" },
            icon("person-add"),
            " Assign",
          );
          b.addEventListener(
            "click",
            () =>
              void post(
                "assign",
                { caregiverId: g.id },
                `Assigned to ${g.name}`,
                () => "Saved as a draft · publish to tell them",
              ),
          );
          return b;
        }),
        el("div", { style: "margin-top:8px" }, pickAny.el),
      )
    : null;
  const activity = el(
    "section",
    { class: "card", "aria-label": "Activity" },
    el("h2", {}, "Activity"),
    ...(s.activity.length
      ? s.activity.map((a) =>
          el(
            "div",
            { class: "prow" },
            el(
              "span",
              {},
              a.words,
              el("br"),
              el("span", { class: "small muted" }, `${a.by} · ${a.at}`),
            ),
          ),
        )
      : [muted("Nothing yet.")]),
  );
  const msg = field("Message", "", { placeholder: v.caregiver.id ? "Write to the caregiver" : "" });
  const send = el("button", { type: "button", class: "btn sm" }, icon("paper-airplane"), " Send");
  const chatList = el("div", { "aria-live": "polite" });
  // Brief 1E-32: newest first; earlier messages fold under "Show n earlier".
  const chatFold = { all: false };
  const drawChat = (list: VisitSheet["chat"]) => {
    const { shown, earlier } = threadShown(list, chatFold.all);
    const more = earlier
      ? el("button", { type: "button", class: "btn sm ghost earlier" }, earlierLabel(earlier))
      : null;
    more?.addEventListener("click", () => {
      chatFold.all = true;
      drawChat(list);
    });
    chatList.replaceChildren(
      ...shown.map((c) =>
        el(
          "div",
          { class: "prow" },
          el(
            "span",
            {},
            el("b", {}, c.who),
            `: ${c.body}`,
            el("br"),
            el("span", { class: "small muted" }, c.at),
          ),
        ),
      ),
      ...(more ? [more] : []),
    );
  };
  drawChat(s.chat);
  send.addEventListener("click", async () => {
    const r = await api<{ chat: VisitSheet["chat"]; sent: string }>(
      "POST",
      `/api/visits/${v.id}/chat`,
      { body: msg.input.value },
    );
    if (!r.ok) return refuse(m.note, r.body.error);
    msg.input.value = "";
    drawChat(r.body.chat);
    toast(
      r.body.sent === "text" ? "Sent" : "Kept as a note",
      r.body.sent === "text"
        ? `To ${v.caregiver.name} by text`
        : "No text line or mobile yet: the team sees it on the visit",
    );
  });
  const chat = el(
    "section",
    { class: "card", "aria-label": "Chat about this visit" },
    el("h2", {}, icon("comment"), " Chat about this visit"),
    v.caregiver.id && s.canSchedule
      ? el(
          "div",
          {},
          msg.el,
          send,
          s.chatBy === "note"
            ? el(
                "p",
                { class: "small muted" },
                "No text line or mobile on file yet: it's kept as a note on the visit.",
              )
            : null,
        )
      : el("p", { class: "muted small" }, "Assign a caregiver to chat."),
    chatList,
  );
  const m = openModal({
    title: `${v.patient.name} · ${v.weekday} ${v.date.slice(0, 5)} · ${v.hours}`,
    sub: `${v.service} · ${v.caregiver.id ? v.caregiver.label : "No caregiver yet"}${s.fromPlan ? " · from the care plan" : ""}`,
    body: [
      el(
        "div",
        {
          style:
            "display:flex;gap:6px;flex-wrap:wrap;align-items:center;justify-content:space-between",
        },
        s.patientPhoto
          ? el(
              "span",
              { class: "nv-face" },
              photoImg(s.patientPhoto, `Photo of ${v.patient.name}`, "pav big"),
              status,
            )
          : status,
        el("span", { class: "vacts" }, ...acts),
      ),
      flagChips(v.flags) ?? el("span"),
      ...(s.whoPays ? [whoPaysLine(v.id, s.whoPays, true, again)] : []),
      ...(orientationNote ? [orientationNote] : []),
      ...(suggestions ? [suggestions] : []),
      el("div", { class: "grid2", style: "margin-top:10px" }, activity, chat),
    ],
    actions: [patientBtn],
  });
  patientBtn.addEventListener("click", () => {
    m.close();
    ctx.go(`/homecare/patients/${v.patient.id}`);
  });
}

/**
 * Who pays on a visit or ride (brief 1E-19): the contract and why (by default, or changed with the
 * reason); Change picks another of the patient's contracts and asks why.
 */
function whoPaysLine(
  visitId: string,
  w: NonNullable<VisitSheet["whoPays"]>,
  canChange: boolean,
  done: () => void,
): HTMLElement {
  const change = el("button", { type: "button", class: "btn sm" }, icon("pencil"), " Change");
  change.addEventListener("click", () => {
    const others = w.choices.filter((c) => c.value !== w.contractId);
    const pick = select(
      "Who pays",
      others.map((c) => [c.value, c.label] as const),
      others[0]?.value ?? "",
    );
    const why = area("Why", "");
    const save = el("button", { type: "button", class: "btn pri" }, "Change who pays");
    const m = openModal({
      title: "Change who pays",
      sub: "For this visit only. The rest follow By default.",
      body: [el("div", { class: "form", style: "max-width:none" }, pick.el, why.el)],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const r = await api<{ whoPays: { words: string } }>(
        "PUT",
        `/api/visits/${visitId}/who-pays`,
        {
          contractId: pick.input.value,
          reason: why.input.value,
        },
      );
      save.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast("Who pays changed", r.body.whoPays.words);
      done();
    });
  });
  return el(
    "div",
    { class: "prow", "aria-label": "Who pays", style: "margin-top:8px" },
    el("span", {}, el("b", {}, "Who pays "), w.words),
    canChange && w.choices.length > 1 ? change : el("span"),
  );
}

/**
 * The office records a missed check-out (brief 1D-3b): the real time and a reason. A check-out
 * already there keeps its first time in the history; pay and billing wait until it's recorded.
 */
function recordCheckOut(v: VisitView, reasons: readonly string[], done: () => void) {
  const local = (iso: string) => {
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const ends = new Date(Date.parse(v.startsAt) + v.hoursNum * 3_600_000).toISOString();
  const at = field("Real check-out time", local(ends), { type: "datetime-local" });
  const reason = select(
    "Reason",
    reasons.map((x) => [x, x] as const),
    reasons[0] ?? "",
  );
  const why = area("Why the office is recording it", "");
  const go = el("button", { type: "button", class: "btn pri" }, icon("check"), " Record");
  const m = openModal({
    title: "Record the missed check-out",
    sub: `${v.caregiver.name} · ${v.when}`,
    body: [el("div", { class: "form", style: "max-width:none" }, at.el, reason.el, why.el)],
    actions: [go],
  });
  go.addEventListener("click", async () => {
    const when = new Date(at.input.value);
    if (Number.isNaN(when.getTime())) return refuse(m.note, "Give the real check-out time.");
    const r = await api("POST", `/api/visits/${v.id}/check-out`, {
      at: when.toISOString(),
      reason: reason.input.value,
      why: why.input.value,
    });
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast("Check-out recorded", "Pay and billing use this time");
    done();
  });
}

// ---- Home Care › Visits (mockup homecare/visits v38) --------------------------------------------------------

interface Board {
  today: string;
  week: { start: string; end: string; label: string; days: { day: string; label: string }[] };
  canSchedule: boolean;
  tellOnPublish: boolean;
  hoursLimit: number;
  visits: VisitView[];
  days: { day: string; label: string; hours: number; visits: number }[];
  caregivers: {
    id: string;
    name: string;
    title: string | null;
    hours: number;
    visits: number;
    over: boolean;
  }[];
  summary: {
    hours: number;
    visits: number;
    caregivers: number;
    drafts: number;
    unassigned: number;
  };
  issues: { key: string; label: string; visits: string[]; slots?: string[] }[];
  issueCount: number;
  /** Brief 1E-4: Pay and billing this week (the Money switch). */
  seesMoney?: boolean;
}
/** Pay and billing this week (brief 1E-4, mockup v56 mnyStrip): derived, never stored. */
interface Money {
  week: { start: string };
  projected: { pay: string; overtime: string; bill: string; margin: string };
  actual: string;
  days: { day: string; pay: string; bill: string; margin: string; low: boolean }[];
  overtimeOn: boolean;
  chips: { personId: string; text: string }[];
  rule: string;
  visits: { id: string; tip: string }[];
}

/** Whether this person hid the strip (their own choice, kept in this browser only). */
const MONEY_KEY = "flowcare.schedule.money";
function moneyShown(): boolean {
  try {
    return localStorage.getItem(MONEY_KEY) !== "hidden";
  } catch {
    return true;
  }
}
function setMoneyShown(on: boolean) {
  try {
    if (on) localStorage.removeItem(MONEY_KEY);
    else localStorage.setItem(MONEY_KEY, "hidden");
  } catch {
    // Without storage the choice lasts until the page is reloaded.
  }
}

/** The strip above the week board: the week's totals, by day, and the overtime chips. */
function moneyStrip(
  m: Money,
  days: Board["days"],
  shown: boolean,
  toggle: () => void,
): HTMLElement {
  if (!shown)
    return el(
      "div",
      { style: "display:flex;justify-content:flex-end" },
      ib("eye", "Show pay and billing (only roles with Sees financials)", toggle),
    );
  return el(
    "section",
    { class: "card", style: "margin:0 0 10px", "aria-label": "Pay and billing this week" },
    el(
      "div",
      { class: "cardhead" },
      el(
        "div",
        {},
        el("h2", {}, icon("graph"), " Pay and billing this week"),
        el(
          "span",
          { class: "small muted" },
          "Projected from the scheduled visits, using each person's pay and each payer's rate.",
        ),
      ),
      el("span", { class: "actions" }, ib("eye-closed", "Hide pay and billing", toggle)),
    ),
    el(
      "div",
      { class: "vksum" },
      el("span", {}, "Projected pay", el("b", {}, m.projected.pay)),
      el("span", {}, "of it overtime", el("b", {}, m.projected.overtime)),
      el("span", {}, "Projected billing", el("b", {}, m.projected.bill)),
      el("span", {}, "Margin", el("b", {}, m.projected.margin)),
      el("span", { class: "muted" }, "Actual so far (validated)", el("b", {}, m.actual)),
    ),
    el(
      "div",
      {
        class: "vkwrap",
        style: "margin-top:8px",
        role: "region",
        "aria-label": "Pay and billing by day",
        tabindex: "0",
      },
      el(
        "div",
        { class: "vkgrid mnygrid", style: `--days:${days.length}` },
        el(
          "div",
          { class: "vkh" },
          el("b", {}, "By day"),
          el("span", { class: "small muted" }, "pay · bill · margin"),
        ),
        ...days.map((d) => {
          const x = m.days.find((y) => y.day === d.day);
          return el(
            "div",
            { class: "mny", "aria-label": d.label },
            el("span", {}, "Pay ", el("b", {}, x?.pay ?? "$0")),
            el("span", {}, "Bill ", el("b", {}, x?.bill ?? "$0")),
            chip(`Margin ${x?.margin ?? "$0"}`, x?.low ? "warn" : "ok"),
          );
        }),
      ),
    ),
    m.overtimeOn
      ? el(
          "div",
          { class: "otl", style: "margin-top:8px" },
          el("b", { class: "small" }, "Overtime"),
          ...(m.chips.length
            ? m.chips.map((c) => chip(c.text, "warn"))
            : [chip("Nobody near overtime", "ok")]),
          el("span", { class: "small muted" }, m.rule),
        )
      : "",
  );
}
interface Month {
  label: string;
  weekdays: string[];
  firstDow: number;
  days: { day: string; n: number; visits: number; hours: number; toFix: boolean }[];
}

type View = "day" | "week" | "month" | "stage" | "val" | "247";
type By = "cg" | "pt" | "client";

/** A segmented choice (mockup seg): buttons, one pressed. */
function seg<T extends string>(label: string, opts: [T, string][], on: T, pick: (v: T) => void) {
  return el(
    "div",
    { class: "seg", role: "group", "aria-label": label },
    ...opts.map(([v, l]) => {
      const b = el("button", { type: "button", "aria-pressed": String(v === on) }, l);
      b.addEventListener("click", () => pick(v));
      return b;
    }),
  );
}

const STATE_ICON: Record<string, string | null> = {
  confirmed: "check-circle",
  cannot_work: "x-circle",
  waiting: "clock",
  draft: null,
  unassigned: null,
};

/** A visit on the board (mockup vchip): dashed for a draft; waiting, confirmed or can't work. */
function vchip(v: VisitView, label: string, open: (id: string) => void, money?: string) {
  const ic = STATE_ICON[v.state];
  const b = el(
    "button",
    {
      type: "button",
      class: `vkchip ${v.state}`,
      // Brief 1E-4: pay, bill and margin on hover, for the Money switch.
      "data-tip": money ? `${money} · ${v.stateTip}` : v.stateTip,
      "aria-label": `${v.hours} · ${label} · ${v.stateLabel}`,
    },
    el("b", {}, v.hours),
    ic ? " " : "",
    ic ? icon(ic) : "",
    el("br"),
    label,
  );
  b.addEventListener("click", () => open(v.id));
  return b;
}

export async function visitsScreen(host: HTMLElement, ctx: ScreenContext) {
  let view: View = "week";
  let by: By = "cg";
  let week: string | null = null;
  let dayAt = -1;
  let notify: boolean | null = null;
  let a: Board | null = null;
  let money: Money | null = null;

  const head = pageHead(ctx, "Schedule, publish and confirm visits at patients' homes.", []);
  const bar = el("div", { class: "vkbar" });
  const sum = el("div", { class: "vksum", "aria-label": "This week" });
  const slot = el("div");
  // Brief 1E-19: appointments during care, a flag for the caregivers and the office.
  const away = el("div");
  void appointmentsDuringCareCard(away, (pid) => ctx.go(`/homecare/patients/${pid}`));
  // "Did you know?" for people in their first days (brief 1D-5), above the summary line.
  host.append(head, away, bar, hintsFor("homecare/visits"), sum, slot);
  const open = (id: string) => void openVisitSheet(ctx, id, () => void load());

  let seq = 0;
  async function load() {
    const mine = ++seq;
    const q = week ? `?week=${week}` : "";
    const res = await api<Board>("GET", `/api/schedule${q}`);
    // A later move (the week arrows pressed again) wins over an answer still on its way.
    if (mine !== seq) return;
    money = null;
    if (res.ok && res.body.seesMoney) {
      const m = await api<Money>("GET", `/api/schedule/money?week=${res.body.week.start}`);
      if (mine !== seq) return;
      money = m.ok ? m.body : null;
    }
    if (!res.ok) {
      slot.replaceChildren(
        el("p", { class: "note", role: "alert" }, "The visits couldn't be loaded."),
      );
      return;
    }
    a = res.body;
    week = a.week.start;
    if (notify === null) notify = a.tellOnPublish;
    if (dayAt < 0) {
      const i = a.week.days.findIndex((d) => d.day === a?.today);
      dayAt = i >= 0 ? i : 0;
    }
    // Back from the next week: its last day.
    if (dayAt >= a.week.days.length) dayAt = a.week.days.length - 1;
    draw();
  }

  function draw() {
    if (!a) return;
    const b = a;
    // The head: the issues button, Publish n with the bell, Schedule visits.
    const issuesBtn = el(
      "button",
      {
        type: "button",
        class: `btn${b.issueCount ? "" : " ghost"}`,
        "data-tip": "What needs fixing this week",
      },
      icon("alert"),
      ` ${b.issueCount} issues`,
    );
    issuesBtn.addEventListener("click", () => issuesModal(b, open));
    const publish = el(
      "button",
      { type: "button", class: "btn pri", disabled: !b.summary.drafts },
      icon("paper-airplane"),
      ` Publish ${b.summary.drafts}`,
    );
    publish.addEventListener("click", () => publishModal(b, !!notify, () => void load()));
    const bell = ib(
      notify ? "bell" : "circle-slash",
      notify
        ? "Caregivers are told on publish (text and app). Click to publish quietly"
        : "Publishing quietly: caregivers aren't told. Click to tell them",
      () => {
        notify = !notify;
        toast(notify ? "Caregivers will be told on publish" : "Publishing quietly");
        draw();
      },
    );
    const schedule = el(
      "button",
      { type: "button", class: "btn" },
      icon("calendar"),
      " Schedule visits",
    );
    schedule.addEventListener("click", () => void scheduleVisits({ done: () => void load() }));
    head
      .querySelector(".actions")
      ?.replaceChildren(
        ...(b.canSchedule
          ? [
              issuesBtn,
              el(
                "span",
                { style: "display:inline-flex;gap:4px;align-items:center" },
                publish,
                bell,
              ),
              schedule,
            ]
          : []),
      );
    // Day, Week, Month, By stage; by caregiver, patient or client; the week arrows.
    // The next week starts the day after this one ends, the last ends the day before it starts:
    // the short changeover week before a new week start is never skipped (briefs 1D-5, 1D-6).
    const move = (n: number) => {
      week = n > 0 ? plusDays(b.week.end, 1) : plusDays(b.week.start, -1);
      void load();
    };
    bar.replaceChildren(
      el(
        "span",
        {},
        seg<View>(
          "View",
          [
            ["day", "Day"],
            ["week", "Week"],
            ["month", "Month"],
            ["stage", "By stage"],
            // Brief 1D-4: To validate and 24/7 coverage, week by week.
            ["val", "To validate"],
            ["247", "24/7"],
          ],
          view,
          (x) => {
            view = x;
            draw();
          },
        ),
        view === "week"
          ? seg<By>(
              "Rows",
              [
                ["cg", "By caregiver"],
                ["pt", "By patient"],
                ["client", "By client"],
              ],
              by,
              (x) => {
                by = x;
                draw();
              },
            )
          : "",
      ),
      view === "stage" || view === "month"
        ? el("span")
        : el(
            "span",
            {},
            ib("arrow-left", "Last week", () => move(-1)),
            el("b", { "aria-live": "polite" }, b.week.label),
            ib("arrow-right", "Next week", () => move(1)),
          ),
    );
    // The summary line with the issues chip.
    if (view === "stage" || view === "val" || view === "247") sum.replaceChildren();
    else {
      const issuesChip = el(
        "button",
        { type: "button", class: `chip ${b.issueCount ? "warn" : "ok"}` },
        b.issueCount ? `${b.issueCount} issues to fix` : "No issues",
      );
      issuesChip.addEventListener("click", () => issuesModal(b, open));
      sum.replaceChildren(
        ...(b.canSchedule ? [issuesChip] : []),
        el("span", {}, "Hours", el("b", {}, String(b.summary.hours))),
        el("span", {}, "Visits", el("b", {}, String(b.summary.visits))),
        el("span", {}, "Caregivers", el("b", {}, String(b.summary.caregivers))),
        el("span", {}, "Drafts", el("b", {}, String(b.summary.drafts))),
        el("span", {}, "Unassigned", el("b", {}, String(b.summary.unassigned))),
        el(
          "span",
          { class: "muted small" },
          icon("check-circle"),
          " confirmed · ",
          icon("clock"),
          " waiting · ",
          icon("x-circle"),
          " can't work · dashed = draft",
        ),
      );
    }
    if (view === "week") {
      const tips = new Map((money?.visits ?? []).map((v) => [v.id, v.tip]));
      const shown = moneyShown();
      const grid = weekGrid(
        b,
        by,
        open,
        () => autoModal(b, open, () => void load()),
        shown ? tips : undefined,
      );
      slot.replaceChildren(
        ...(money
          ? [
              moneyStrip(money, b.days, shown, () => {
                setMoneyShown(!shown);
                draw();
              }),
            ]
          : []),
        grid,
      );
    } else if (view === "day")
      slot.replaceChildren(
        dayList(b, dayAt, open, (n) => {
          const next = dayAt + n;
          if (next < 0 || next >= b.week.days.length) {
            week = next < 0 ? plusDays(b.week.start, -1) : plusDays(b.week.end, 1);
            dayAt = next < 0 ? 99 : 0;
            void load();
          } else {
            dayAt = next;
            draw();
          }
        }),
      );
    else if (view === "month")
      void monthGrid(b, slot, (day) => {
        week = day;
        view = "day";
        dayAt = -1;
        void load().then(() => {
          dayAt = Math.max(0, a?.week.days.findIndex((d) => d.day === day) ?? 0);
          draw();
        });
      });
    else if (view === "val") void validateView(slot, ctx, b.week.start, () => undefined);
    else if (view === "247") void coverageView(slot, ctx, b.week.start, () => undefined);
    else void stageView(ctx, slot, b.canSchedule, () => void load());
  }
  await load();
}

/** The week board (mockup weekGrid): rows by caregiver (Unassigned first), patient or client. */
function weekGrid(
  b: Board,
  by: By,
  open: (id: string) => void,
  auto: () => void,
  tips?: ReadonlyMap<string, string>,
): HTMLElement {
  type Row = {
    key: string | null;
    name: string;
    sub: Node | string;
    vs: VisitView[];
    un?: boolean;
  };
  let rows: Row[];
  // The Unassigned row is the office's: someone who doesn't schedule sees only their own visits.
  if (by === "cg" && !b.canSchedule)
    rows = b.caregivers.map((c) => ({
      key: c.id,
      name: c.name,
      sub: `${c.title ? `${c.title} · ` : ""}${c.hours} hrs · ${c.visits} visits`,
      vs: b.visits.filter((v) => v.caregiver.id === c.id),
    }));
  else if (by === "cg")
    rows = [
      {
        key: null,
        name: "Unassigned",
        sub: `${b.visits.filter((v) => !v.caregiver.id).length} to fill`,
        vs: b.visits.filter((v) => !v.caregiver.id),
        un: true,
      },
      ...b.caregivers.map((c) => {
        const words = `${c.title ? `${c.title} · ` : ""}${c.hours} hrs · ${c.visits} visits`;
        return {
          key: c.id,
          name: c.name,
          sub: c.over ? chip(words, "warn") : words,
          vs: b.visits.filter((v) => v.caregiver.id === c.id),
        };
      }),
    ];
  else if (by === "pt")
    rows = [...new Map(b.visits.map((v) => [v.patient.id, v])).values()]
      .sort((x, y) => x.patient.name.localeCompare(y.patient.name))
      .map((v) => ({
        key: v.patient.id,
        name: v.patient.name,
        sub: v.client,
        vs: b.visits.filter((x) => x.patient.id === v.patient.id),
      }));
  else
    rows = [...new Set(b.visits.map((v) => v.client))].sort().map((c) => ({
      key: c,
      name: c || "No client",
      sub: "",
      vs: b.visits.filter((x) => x.client === c),
    }));
  const label = (v: VisitView) =>
    by === "cg"
      ? `${v.patient.name} · ${v.service}`
      : `${v.caregiver.name}${by === "client" ? ` · ${v.patient.name}` : ""} · ${v.service}`;
  if (!b.visits.length && !b.canSchedule)
    return el("p", { class: "muted" }, "No visits in these days.");
  return el(
    "div",
    { class: "vkwrap", role: "region", "aria-label": "Week board", tabindex: "0" },
    el(
      "div",
      { class: "vkgrid", style: `--days:${b.week.days.length}` },
      el(
        "div",
        { class: "vkh" },
        el("b", {}, by === "cg" ? "Caregiver" : by === "pt" ? "Patient" : "Client"),
        el("span", { class: "small muted" }, "this week"),
      ),
      ...b.days.map((d) =>
        el(
          "div",
          { class: "vkh" },
          el("b", {}, d.label),
          el("span", { class: "small muted" }, `${d.hours} hrs · ${d.visits} visits`),
        ),
      ),
      ...rows.flatMap((r) => [
        el(
          "div",
          { class: `vkrow${r.un ? " vkun" : ""}`, "data-row": r.name },
          el(
            "span",
            {},
            el("b", {}, r.name),
            r.un && b.canSchedule
              ? ib("zap", "Auto-assign these visits (preview first)", auto)
              : "",
          ),
          el("span", { class: "small muted" }, r.sub),
        ),
        ...b.days.map((d) =>
          el(
            "div",
            { class: r.un ? "vkun" : "" },
            ...r.vs
              .filter((v) => v.day === d.day)
              .map((v) => vchip(v, label(v), open, tips?.get(v.id))),
          ),
        ),
      ]),
    ),
  );
}

/** Day (mockup dayList): the day's visits in time order, with the day arrows. */
function dayList(
  b: Board,
  at: number,
  open: (id: string) => void,
  move: (n: number) => void,
): HTMLElement {
  const d = b.days[at] ?? b.days[0];
  const vs = b.visits.filter((v) => v.day === d?.day);
  return el(
    "section",
    { class: "card vkday", "aria-label": "Day" },
    el(
      "div",
      { class: "cardhead" },
      el("h2", {}, d?.label ?? ""),
      el(
        "span",
        { class: "rowacts" },
        ib("arrow-left", "Day before", () => move(-1)),
        ib("arrow-right", "Next day", () => move(1)),
      ),
    ),
    ...(vs.length
      ? vs.map((v) =>
          el(
            "div",
            { class: "prow" },
            el(
              "span",
              {},
              el("b", {}, v.hours),
              ` · ${v.patient.name} `,
              el("span", { class: "small muted" }, `· ${v.service} · ${v.caregiver.name}`),
            ),
            vchip(v, v.stateLabel, open),
          ),
        )
      : [el("p", { class: "muted" }, "No visits.")]),
  );
}

/** Month (mockup monthGrid): each day's visits and hours, "to fix" when one is a draft or unassigned. */
async function monthGrid(b: Board, slot: HTMLElement, openDay: (day: string) => void) {
  const res = await api<Month>("GET", `/api/schedule/month?month=${b.week.start.slice(0, 7)}`);
  if (!res.ok) return;
  const m = res.body;
  slot.replaceChildren(
    el(
      "section",
      { class: "card", "aria-label": "Month" },
      el(
        "div",
        { class: "cardhead" },
        el("h2", {}, m.label),
        el("span", { class: "small muted" }, "Click a day to open it"),
      ),
      el(
        "div",
        { class: "vkmonth" },
        ...m.weekdays.map((x) => el("b", { class: "small muted" }, x)),
        ...Array.from({ length: m.firstDow }, () => el("div", { class: "blank" })),
        ...m.days.map((d) => {
          const btn = el(
            "button",
            { type: "button", "aria-label": `${d.day}: ${d.visits} visits` },
            el("b", {}, String(d.n)),
            el("br"),
            el("span", { class: "small muted" }, `${d.visits} visits · ${d.hours} hrs`),
            d.toFix ? el("span", {}, el("br"), chip("to fix", "warn")) : "",
          );
          btn.addEventListener("click", () => openDay(d.day));
          return btn;
        }),
      ),
    ),
  );
}

/** This week's issues (mockup vkIssues): each kind with its visits, each with Open. */
function issuesModal(b: Board, open: (id: string) => void) {
  const of = (id: string) => b.visits.find((v) => v.id === id);
  const body = b.issues.length
    ? b.issues.map((i) =>
        el(
          "section",
          { class: "card", style: "margin:8px 0 0", "aria-label": i.label },
          el(
            "div",
            { class: "cardhead" },
            el("h2", {}, i.label),
            chip(String(i.visits.length + (i.slots?.length ?? 0)), "warn"),
          ),
          // A 24/7 slot nobody works has no visit to open (brief 1D-4): it's named.
          ...(i.slots ?? []).map((words) =>
            el("div", { class: "prow" }, el("span", {}, el("b", {}, words))),
          ),
          ...i.visits.map((id) => {
            const v = of(id);
            const go = el("button", { type: "button", class: "btn sm" }, icon("eye"), " Open");
            go.addEventListener("click", () => {
              m.close();
              open(id);
            });
            return el(
              "div",
              { class: "prow" },
              el(
                "span",
                {},
                el("b", {}, v?.patient.name ?? "Visit"),
                ` · ${v?.weekday ?? ""} ${v?.date.slice(0, 5) ?? ""} · ${v?.hours ?? ""}`,
                el("br"),
                el("span", { class: "small muted" }, v?.caregiver.name ?? ""),
              ),
              go,
            );
          }),
        ),
      )
    : [el("p", { class: "muted" }, "Nothing to fix this week.")];
  const m = openModal({
    title: "This week's issues",
    sub: "Each one is a flag on the visit. Click a visit to fix it.",
    body,
    wide: true,
  });
}

/** Publish n visits (mockup vkPub): the drafts by caregiver, tell or not, offer the unassigned. */
function publishModal(b: Board, tell: boolean, done: () => void) {
  const drafts = b.visits.filter((v) => v.state === "draft");
  const by = new Map<string, VisitView[]>();
  for (const v of drafts) by.set(v.caregiver.name, [...(by.get(v.caregiver.name) ?? []), v]);
  const tellBox = el("input", { type: "checkbox", id: "vk-tell", checked: tell });
  const openBox = el("input", { type: "checkbox", id: "vk-open", checked: true });
  const go = el("button", { type: "button", class: "btn pri" }, icon("paper-airplane"), " Publish");
  const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
  const unassigned = b.summary.unassigned;
  const m = openModal({
    title: `Publish ${drafts.length} visits`,
    sub: "Published visits show in each caregiver's app, where they confirm or say they can't work.",
    body: [
      ...[...by.entries()].map(([name, vs]) =>
        el(
          "div",
          { class: "prow" },
          el(
            "span",
            {},
            el("b", {}, name),
            el("br"),
            el(
              "span",
              { class: "small muted" },
              vs.map((v) => `${v.weekday} ${v.hours} · ${v.patient.name}`).join(" | "),
            ),
          ),
          chip(`${vs.length} visits`, "info"),
        ),
      ),
      unassigned
        ? el(
            "div",
            { class: "note" },
            el(
              "p",
              {},
              `${unassigned} unassigned visits aren't published. Assign them or offer them as open visits.`,
            ),
          )
        : el("span"),
      el(
        "label",
        { class: "ptask", for: "vk-tell" },
        tellBox,
        " Tell caregivers by text and in the app",
      ),
      unassigned
        ? el(
            "label",
            { class: "ptask", for: "vk-open" },
            openBox,
            " Offer the unassigned visits as open visits caregivers can claim",
          )
        : el("span"),
    ],
    actions: [go, cancel],
  });
  cancel.addEventListener("click", () => m.close());
  go.addEventListener("click", async () => {
    go.disabled = true;
    const r = await api<{ published: number; words: string }>("POST", "/api/schedule/publish", {
      week: b.week.start,
      tell: tellBox.checked,
      offerOpen: !!unassigned && openBox.checked,
    });
    go.disabled = false;
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast(`Published ${r.body.published} visits`, r.body.words);
    done();
  });
}

/** Auto-assign · preview (mockup vkAuto): each unassigned visit's top pick; nothing changes until Accept. */
async function autoModal(b: Board, open: (id: string) => void, done: () => void) {
  const res = await api<{ visits: { visit: VisitView; pick: Sug | null; others: Sug[] }[] }>(
    "GET",
    `/api/schedule/auto?week=${b.week.start}`,
  );
  if (!res.ok) return toast("Auto-assign isn't available", res.body.error);
  const rows = res.body.visits.filter((x) => x.pick);
  const chosen = new Map(rows.map((x) => [x.visit.id, x.pick as Sug]));
  const boxes = new Map<string, HTMLInputElement>();
  const line = (x: (typeof rows)[number]) => {
    const box = el("input", {
      type: "checkbox",
      checked: true,
      "aria-label": `Accept this pick for ${x.visit.patient.name} ${x.visit.weekday}`,
    });
    boxes.set(x.visit.id, box);
    const who = el("div");
    const drawWho = () => {
      const g = chosen.get(x.visit.id) as Sug;
      who.replaceChildren(
        el("b", {}, x.visit.patient.name),
        ` · ${x.visit.weekday} ${x.visit.date.slice(0, 5)} · ${x.visit.hours} → `,
        el("b", {}, g.name),
        g.reasons.length ? el("div", { class: "small muted" }, g.reasons.join(" · ")) : "",
        g.notes.length
          ? el("div", { class: "small why miss" }, `Note: ${g.notes.join(" · ")}`)
          : "",
      );
    };
    drawWho();
    const pickers = [x.pick as Sug, ...x.others];
    const swap = select(
      "Pick someone else",
      pickers.map((p) => [p.id, `${p.name}${p.title ? ` (${p.title})` : ""}`] as const),
      x.pick?.id ?? "",
    );
    swap.el.style.display = "none";
    swap.input.addEventListener("change", () => {
      const p = pickers.find((y) => y.id === swap.input.value);
      if (p) chosen.set(x.visit.id, p);
      drawWho();
    });
    const sw = ib("arrow-switch", "Pick someone else", () => {
      swap.el.style.display = swap.el.style.display === "none" ? "" : "none";
    });
    return el(
      "div",
      { class: "sg auto", style: "margin-top:8px" },
      box,
      el("div", {}, who, swap.el),
      sw,
    );
  };
  const go = el(
    "button",
    { type: "button", class: "btn pri" },
    icon("check"),
    " Accept checked (as drafts)",
  );
  const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
  const m = openModal({
    title: "Auto-assign · preview",
    sub: `${res.body.visits.length} unassigned visits. Nothing changes until the picks are accepted. Each pick shows why.`,
    body: [
      el("div", { class: "vksg" }, ...rows.map(line)),
      rows.length ? el("span") : el("p", { class: "muted" }, "No unassigned visits this week."),
    ],
    actions: [go, cancel],
    wide: true,
  });
  cancel.addEventListener("click", () => m.close());
  void open;
  go.addEventListener("click", async () => {
    const picks = rows
      .filter((x) => boxes.get(x.visit.id)?.checked)
      .map((x) => ({ visitId: x.visit.id, caregiverId: (chosen.get(x.visit.id) as Sug).id }));
    const r = await api<{ assigned: number }>("POST", "/api/schedule/auto", { picks });
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast(`Assigned ${r.body.assigned} visits as drafts`, "Review, then Publish");
    done();
  });
}

interface Answer extends PageInfo {
  from: string;
  to: string;
  stages: string[];
  statuses: string[];
  canSchedule: boolean;
  visits: VisitView[];
}

/** By stage (the 1D-1 board and list): Scheduled to Validated, with From and To. */
async function stageView(
  ctx: ScreenContext,
  slot: HTMLElement,
  canSchedule: boolean,
  changed: () => void,
) {
  const from = field("From", "", { type: "date" });
  const to = field("To", "", { type: "date" });
  const listSlot = el("div");
  slot.replaceChildren(
    el("div", { class: "formgrid", style: "max-width:420px;margin:0 0 8px" }, from.el, to.el),
    listSlot,
  );
  async function load() {
    const q = new URLSearchParams();
    if (from.input.value) q.set("from", from.input.value);
    if (to.input.value) q.set("to", to.input.value);
    // Brief 1E-32: the server answers one page at a time, for these days.
    const ask = (params: URLSearchParams) => {
      const all = new URLSearchParams(q);
      for (const [k, v] of params) all.set(k, v);
      return api<Answer>("GET", `/api/visits?${all.toString()}`);
    };
    const res = await ask(firstPageParams("homecare/visits", ctx.me.user.id));
    if (!res.ok) {
      listSlot.replaceChildren(
        el("p", { class: "note", role: "alert" }, "The visits couldn't be loaded."),
      );
      return;
    }
    const a = res.body;
    from.input.value = a.from;
    to.input.value = a.to;
    const columns: Column<VisitView>[] = [
      {
        id: "patient",
        label: "Patient",
        text: (v) => v.patient.name,
        cell: (v) => el("b", {}, v.patient.name),
        search: true,
      },
      { id: "caregiver", label: "Caregiver", text: (v) => v.caregiver.label, search: true },
      { id: "when", label: "When", text: (v) => `${v.date} ${v.when}`, cell: (v) => v.when },
      { id: "client", label: "Client", text: (v) => v.client, search: true },
      {
        id: "flagged",
        label: "Flags",
        text: (v) => (v.flagged ? "Flagged" : "Not flagged"),
        cell: (v) =>
          v.flagged
            ? chip(`${v.flags.length} flag${v.flags.length === 1 ? "" : "s"}`, "warn")
            : muted("–"),
        values: ["Flagged", "Not flagged"],
      },
      { id: "stage", label: "Stage", text: (v) => v.stage, cell: stageChip, values: a.statuses },
    ];
    listSlot.replaceChildren(
      list<VisitView>({
        id: "homecare/visits",
        userId: ctx.me.user.id,
        noun: "visits",
        searchLabel: "Search patients, caregivers or clients",
        first: { rows: a.visits, page: a },
        source: async (params) => {
          const r = await ask(params);
          return r.ok ? { rows: r.body.visits, page: r.body } : null;
        },
        rowId: (v) => v.id,
        columns,
        chips: { values: a.statuses, of: (v) => v.stage },
        board: {
          stages: a.stages,
          stageOf: (v) => v.stage,
          tile: (v) => [v.patient.name, v.caregiver.label, v.when],
        },
        onOpen: (v) =>
          void openVisitSheet(ctx, v.id, () => {
            void load();
            changed();
          }),
        empty: canSchedule
          ? "No visits in these days. Schedule visits sets them up from the care plan."
          : "No visits in these days.",
      }),
    );
  }
  for (const i of [from.input, to.input]) i.addEventListener("change", () => void load());
  await load();
}

// ---- The patient's Visits tab --------------------------------------------------------------------------

interface TabAnswer {
  canSchedule: boolean;
  /** The episode's 24/7 switch (brief 1D-5); null without an episode. */
  roundTheClock: boolean | null;
  thisWeek: number;
  upcoming: VisitView[];
  past: VisitView[];
}

/** The patient's Visits tab (mockup caseRec "visits"): upcoming and past, with Schedule visit. */
export async function patientVisitsTab(
  host: HTMLElement,
  patientId: string,
  changed: () => void,
): Promise<{ reload: () => Promise<void> }> {
  let show: "upcoming" | "past" = "upcoming";
  async function reload() {
    const res = await api<TabAnswer>("GET", `/api/patients/${patientId}/visits`);
    if (!res.ok) {
      host.replaceChildren(
        el(
          "section",
          { class: "card" },
          el("p", { class: "muted" }, "Visits need Visits at View."),
        ),
      );
      return;
    }
    const a = res.body;
    const after = () => {
      void reload();
      changed();
    };
    const schedule = ib(
      "plus",
      "Schedule visit",
      () => void scheduleVisits({ patientId, done: after }),
    );
    const chips = el(
      "div",
      { class: "stagef", role: "group", "aria-label": "Show" },
      ...(
        [
          ["upcoming", `Upcoming ${a.upcoming.length}`],
          ["past", `Past ${a.past.length}`],
        ] as const
      ).map(([k, l]) => {
        const b = el(
          "button",
          {
            type: "button",
            class: `chipbtn${show === k ? " on" : ""}`,
            "aria-pressed": String(show === k),
          },
          l,
        );
        b.addEventListener("click", () => {
          show = k;
          void reload();
        });
        return b;
      }),
    );
    const rows = show === "upcoming" ? a.upcoming : a.past;
    const team = el("div");
    void careTeamCard(team, patientId, after);
    // Brief 1E-19: each upcoming appointment during care, its caregivers asked and their answers.
    const away = el("div");
    void patientAppointmentCards(away, patientId);
    // Round-the-clock care is a switch on the episode (brief 1D-5): the 24/7 view and its slots
    // follow it.
    let clock: HTMLElement | null = null;
    if (a.roundTheClock !== null) {
      const box = el("input", {
        type: "checkbox",
        checked: a.roundTheClock,
        disabled: !a.canSchedule,
      });
      box.addEventListener("change", async () => {
        box.disabled = true;
        const r = await api<{ on: boolean }>("PUT", `/api/patients/${patientId}/round-the-clock`, {
          on: box.checked,
        });
        box.disabled = false;
        if (!r.ok) {
          box.checked = !box.checked;
          return toast("That wasn't saved", r.body.error);
        }
        toast(
          r.body.on ? "Round-the-clock care is on" : "Round-the-clock care is off",
          r.body.on
            ? "Its shifts show on Schedule › 24/7"
            : "It no longer shows on Schedule › 24/7",
        );
        changed();
      });
      clock = el("label", { class: "ptask" }, box, " Round-the-clock care (24/7)");
    }
    host.replaceChildren(
      away,
      card(
        "Visits",
        null,
        a.canSchedule ? [schedule] : [],
        el(
          "div",
          {},
          chips,
          rows.length
            ? table(
                [
                  {
                    h: "When",
                    f: (v: VisitView) => el("span", {}, el("b", {}, v.when), flagChips(v.flags)),
                  },
                  {
                    h: "Who",
                    f: (v) => el("span", {}, `${v.caregiver.label} · ${v.service}`),
                  },
                  { h: "Billed by", f: (v) => v.billed },
                  {
                    h: "Stage",
                    f: (v) =>
                      el(
                        "span",
                        {},
                        stageChip(v),
                        v.supervisory ? " " : "",
                        v.supervisory ? chip("Supervisory", "info") : "",
                        v.cancelled
                          ? el(
                              "div",
                              { class: "small muted" },
                              `${v.cancelled.byWhom}: ${v.cancelled.why}`,
                            )
                          : null,
                      ),
                  },
                  { h: "", f: (v) => (a.canSchedule ? visitActions(v, after) : "") },
                ],
                rows,
                undefined,
                "Visits",
              )
            : el(
                "p",
                { class: "muted" },
                show === "upcoming" ? "No visits yet." : "No past visits.",
              ),
          el(
            "p",
            { class: "small muted" },
            "Aides are billed by the hour; RN and LPN by the visit.",
          ),
          clock,
        ),
      ),
      hintsFor("homecare/patient-visits"),
      team,
    );
  }
  await reload();
  return { reload };
}
